import type { ModuleSettings } from '../ECommercePage';
import type { BrandFlowSettings } from './brandFlow/types';
import type { ShowcaseButton, ShowcaseSettings } from './showcase/types';

export type HomepageModuleTemplateId =
  | 'default'
  | 'category-row'
  | 'brand-flow'
  | 'showcase'
  | 'showcase-banner';

const showcaseButton = (
  id: string,
  label: string,
  variant: ShowcaseButton['variant'],
  icon: ShowcaseButton['icon'] = 'none',
  caption = ''
): ShowcaseButton => ({ id, label, caption, href: '', variant, icon });

export const DEFAULT_SHOWCASE_SPLIT: ShowcaseSettings = {
  layout: 'split',
  imageSide: 'right',
  imageUrl: '',
  eyebrow: 'Kısa etiket',
  title: 'Başlığınız burada',
  highlight: 'vurgulu satır.',
  description: 'Açıklama metnini buraya yazın. Görsel sağda veya solda durabilir.',
  note: 'İsteğe bağlı ikinci paragraf.',
  backgroundColor: '#f6f1e7',
  gradientColor: '#f6f1e7',
  titleColor: '#0c2340',
  highlightColor: '#c4a46a',
  textColor: '#5c6570',
  buttons: [
    showcaseButton('store-1', 'App Store', 'dark', 'apple', 'İNDİRİN'),
    showcaseButton('store-2', 'Google Play', 'dark', 'play', 'İNDİRİN'),
  ],
};

export const DEFAULT_SHOWCASE_BANNER: ShowcaseSettings = {
  layout: 'banner',
  imageSide: 'right',
  imageUrl: '',
  eyebrow: '',
  title: 'Başlığınız burada',
  highlight: 'vurgulu satır.',
  description: 'Görselsiz çağrı bandı. Kısa metin ve butonlar ortada durur.',
  note: '',
  backgroundColor: '#0b2a62',
  gradientColor: '#163e8c',
  titleColor: '#ffffff',
  highlightColor: '#e0c27a',
  textColor: '#dbe4f5',
  buttons: [
    showcaseButton('cta-1', 'BİRİNCİ BUTON', 'gold'),
    showcaseButton('cta-2', 'İKİNCİ BUTON', 'outline'),
  ],
};

export const showcaseSettingsFor = (layout: ShowcaseSettings['layout']): ShowcaseSettings =>
  layout === 'banner'
    ? {
        ...DEFAULT_SHOWCASE_BANNER,
        buttons: DEFAULT_SHOWCASE_BANNER.buttons.map((button) => ({ ...button })),
      }
    : {
        ...DEFAULT_SHOWCASE_SPLIT,
        buttons: DEFAULT_SHOWCASE_SPLIT.buttons.map((button) => ({ ...button })),
      };

export const DEFAULT_BRAND_FLOW_SETTINGS: BrandFlowSettings = {
  backgroundColor: '#F3F6F3',
  subtitle: '',
  subtitleStyles: {
    fontFamily: 'Inter',
    fontSize: 12,
    color: '#B8A07A',
    bold: true,
    italic: false,
  },
  footerText: '',
  footerStyles: {
    fontFamily: 'Inter',
    fontSize: 13,
    color: '#9CA3AF',
    bold: false,
    italic: false,
  },
  speedSeconds: 40,
  logoHeight: 36,
  cardBackground: '#FFFFFF',
  gapPx: 16,
  pauseOnHover: true,
  source: 'inventory',
  brandIds: [],
  items: [],
};

export const applyHomepageModuleTemplate = (
  base: ModuleSettings,
  template: HomepageModuleTemplateId
): ModuleSettings => {
  if (template === 'brand-flow') {
    return {
      ...base,
      productSource: 'manual',
      moduleTemplate: 'brand-flow',
      selectedProductIds: [],
      selectedCategoryIds: [],
      titleStyles: {
        ...base.titleStyles,
        fontSize: 32,
        color: '#1a472a',
        bold: true,
        italic: false,
        fontFamily: 'Georgia',
        textAlign: 'center',
      },
      brandFlow: {
        ...DEFAULT_BRAND_FLOW_SETTINGS,
        ...(base.brandFlow || {}),
      },
    };
  }

  if (template === 'showcase' || template === 'showcase-banner') {
    const showcase = showcaseSettingsFor(template === 'showcase-banner' ? 'banner' : 'split');
    return {
      ...base,
      productSource: 'manual',
      moduleTemplate: 'showcase',
      selectedProductIds: [],
      selectedCategoryIds: [],
      horizontalPosition: 50,
      showcase,
      htmlContent: JSON.stringify(showcase),
    };
  }

  if (template !== 'category-row') {
    return {
      ...base,
      productSource: 'manual',
      moduleTemplate: 'default',
    };
  }

  const currentImage = base.moduleImage || {
    imageUrl: '',
    width: 600,
    height: 400,
    alignment: 'center' as const,
    productLayout: { rows: 1, columns: 3 },
  };

  return {
    ...base,
    productSource: 'category',
    moduleTemplate: 'category-row',
    showViewAllButton: true,
    viewAllButtonText: 'Tümü',
    categoryProductLimit: 3,
    maxProducts: 3,
    selectedProductIds: [],
    selectedCategoryIds: base.selectedCategoryIds || [],
    titleStyles: {
      ...base.titleStyles,
      textAlign: 'left',
    },
    moduleImage: {
      ...currentImage,
      productLayout: { rows: 1, columns: 3 },
      productCardStyle: {
        ...(currentImage.productCardStyle || {}),
        cardEnabled: true,
        displayMode: 'grid',
        showProductName: true,
        showPrice: true,
      },
    },
  };
};
